import { lazy, Suspense, useEffect } from 'react';
import { createFileRoute, Outlet, redirect, useRouterState } from '@tanstack/react-router';
import { useAuthStore } from '@/stores/auth.store';
import { isTokenExpired } from '@/lib/auth';
import { recoverSession, redirectToLogin, startSessionKeepAlive } from '@/lib/api/session';
import { hasCallsInFlight } from '@/lib/voip/callsInFlight';
import { Sidebar } from '@/components/layout/Sidebar';
import { Header } from '@/components/layout/Header';
import { CommandPalette } from '@/components/layout/CommandPalette';
import { MobileBottomBar } from '@/components/layout/MobileBottomBar';
import { ImpersonationBanner } from '@/components/layout/ImpersonationBanner';
import { OnboardingReminderBanner } from '@/components/layout/OnboardingReminderBanner';
import { CustomerCommsHoldBanner } from '@/components/layout/CustomerCommsHoldBanner';
import { NotificationRuntime } from '@/components/layout/GlobalNotificationBell';
import { FullbleedProvider, useFullbleed } from '@/contexts/fullbleed';
import { LiveCallsProvider } from '@/contexts/live-calls';
import { DashboardHeaderProvider } from '@/contexts/dashboard-header';
import { FeatureFlagsSync } from '@/features/feature-flags/FeatureFlagsSync';
import { RemoteAudio } from '@/components/voip/RemoteAudio';
import { CallRingtone } from '@/components/voip/CallRingtone';
import { CallerContactResolver } from '@/components/voip/CallerContactResolver';
import { useVoipRegistration } from '@/hooks/voip/useVoipRegistration';
import { useInboxStream } from '@/hooks/messaging/useInboxStream';
import { useAudioDeviceRecovery } from '@/hooks/voip/useAudioDeviceRecovery';
import { useCallPresence } from '@/hooks/voip/useCallPresence';
import { isSettingsWorkspacePath, SettingsWorkspace } from '@/components/layout/SettingsWorkspace';

// Lazy-load VoIP components so @telnyx/webrtc and socket.io-client are not
// in the initial bundle — they only load after the dashboard shell renders.
const CallHUD = lazy(() =>
  import('@/components/voip/CallHUD').then((m) => ({ default: m.CallHUD })),
);
const TransferFailedBanner = lazy(() =>
  import('@/components/voip/TransferFailedBanner').then((m) => ({
    default: m.TransferFailedBanner,
  })),
);
const LiveTranscriptDock = lazy(() =>
  import('@/components/voip/LiveTranscriptDock').then((m) => ({
    default: m.LiveTranscriptDock,
  })),
);
const IncomingCallToast = lazy(() =>
  import('@/components/voip/IncomingCallToast').then((m) => ({
    default: m.IncomingCallToast,
  })),
);
// Conference lives at the dashboard root so it survives navigation and can be
// minimized to a floating window while the user works elsewhere.
const ConferenceOverlay = lazy(() =>
  import('@/components/conference/ConferenceOverlay').then((m) => ({
    default: m.ConferenceOverlay,
  })),
);
// The live-call bubble and its monitoring sheet. Lazy because most of what it
// shows (transcript, listening) is only reached by opening it; the state it
// reads from is in LiveCallsProvider, which is eager and tiny.
const GlobalLiveCalls = lazy(() =>
  import('@/components/calls/live/global/GlobalLiveCalls').then((m) => ({
    default: m.GlobalLiveCalls,
  })),
);

export const Route = createFileRoute('/_dashboard')({
  beforeLoad: async ({ location }) => {
    const { token, logout } = useAuthStore.getState();
    if (token && isTokenExpired(token)) {
      // Renew (or adopt another tab's renewal) before treating it as over.
      if (await recoverSession(token)) return;
      // Over, but a call is up: let the page load and sign out after it ends.
      // Logging out here would hang up on the caller.
      if (hasCallsInFlight()) {
        redirectToLogin();
        return;
      }
    }
    if (!token || isTokenExpired(token)) {
      logout();
      throw redirect({
        to: '/login',
        search: { redirect: location.href },
        replace: true,
      });
    }
  },
  component: DashboardLayout,
});

function DashboardLayout() {
  // Register the Telnyx softphone for the lifetime of the dashboard so inbound
  // calls can ring this tab; unregisters on logout / leaving the dashboard.
  useVoipRegistration();
  // The softphone is mounted here, so its event stream has to be too.
  //
  // It used to be opened by the Inbox and the call-log workspace, which meant
  // an agent answering a call from Jobs or Schedule had no stream at all — and
  // `call.bridged`, which is what tells the call bar the customer can actually
  // hear them, never arrived. The bar then had nothing to wait on but a timer,
  // and a timer is wrong in both directions. One connection per tab, open
  // wherever a call can be answered, which is everywhere below this layout.
  //
  // Ungated on purpose, and it took a round of review to get here. Gating it on
  // `messages:view` stopped the 403 retry loop but left the gap it was meant to
  // close: `useVoipRegistration` above is not gated on anything, so a user
  // without that grant can still be rung and can still answer — they were
  // simply the one person who could not be told their caller was connected.
  // The stream now admits any tenant-authenticated subscriber and decides what
  // they may see; without `messages:view` that is `call.bridged` alone, reduced
  // to leg ids. See MessagingEventsController.
  useInboxStream();
  // Keep a live call on a working headset when the OS device list changes —
  // mounted here, with the rest of the global call plumbing, so it covers every
  // call regardless of which page the CSR is on.
  useAudioDeviceRecovery();
  // Telnyx rings every tab the user has open; this is how they find out about
  // each other, so the idle one beeps instead of ringing over the live call.
  useCallPresence();
  // Renew the 8-hour access token before it lapses, and adopt renewals made
  // by other tabs, so a working day does not end in a forced sign-out.
  useEffect(() => startSessionKeepAlive(), []);

  // Outer column wrapper hosts the impersonation banner above the
  // sidebar+content row. The banner renders nothing when impersonation is
  // inactive, so this adds zero height to the layout for normal sessions.
  return (
    <FullbleedProvider>
      {/* One live-calls stream for the tab, above every page: the bubble, the
          monitoring sheet, the header count and the Live calls page all read it,
          so they can never disagree about what is on the phones. Opens nothing
          without `calls:view` at ALL scope. */}
      <LiveCallsProvider>
        <DashboardHeaderProvider>
          <div className="flex h-screen flex-col overflow-hidden bg-canvas">
            <ImpersonationBanner />
            <CustomerCommsHoldBanner />
            <OnboardingReminderBanner />
            {/* Keeps the tenant's feature flags fresh for every page (PLAT-FF-1). */}
            <FeatureFlagsSync />
            {/* In the column, not fixed over it: while a call is up the bar takes
              its own height and pushes the sidebar and header down, instead of
              sitting on top of the header and hiding its controls. */}
            <Suspense>
              {/* Outside the HUD on purpose: a failed transfer ends the agent's
                leg, and the notice that the caller went to voicemail has to
                survive the HUD unmounting with it. */}
              <TransferFailedBanner />
              <CallHUD />
            </Suspense>
            <div className="flex flex-1 min-h-0 overflow-hidden">
              <Sidebar />
              <NotificationRuntime />
              <DashboardContentWrapper />
              {/* A real column beside the content, not a panel over it: while a
                call is up the page reflows into the width that is left, so the
                record being discussed and the transcript are both usable. */}
              <Suspense>
                <LiveTranscriptDock />
              </Suspense>
              <MobileBottomBarWrapper />
              <CommandPalette />
              <Suspense>
                <IncomingCallToast />
                {/* Global on purpose: the conference survives navigation and can
                  be minimized to a floating window while the room stays live. */}
                <ConferenceOverlay />
                {/* Active calls, reachable from every page: a bubble that opens a
                  monitoring sheet. Read-only — it cannot touch a call. */}
                <GlobalLiveCalls />
              </Suspense>
              <RemoteAudio />
              <CallRingtone />
              <CallerContactResolver />
            </div>
          </div>
        </DashboardHeaderProvider>
      </LiveCallsProvider>
    </FullbleedProvider>
  );
}

function DashboardContentWrapper() {
  return (
    <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
      <Header />
      <MainArea />
    </div>
  );
}

function MobileBottomBarWrapper() {
  // Always show on mobile — fullbleed only affects desktop layout
  return <MobileBottomBar />;
}

function MainArea() {
  const { fullbleed, flush } = useFullbleed();
  // Match Outlet's active page, not a destination that is still loading.
  // Changing its parent early remounts the outgoing page and replays its effects.
  const pathname = useRouterState({
    select: (state) => state.matches[state.matches.length - 1]?.pathname ?? state.location.pathname,
  });
  const settingsWorkspace = isSettingsWorkspacePath(pathname);

  return (
    <main
      className={
        settingsWorkspace
          ? 'flex flex-1 min-h-0 overflow-hidden border bg-background pb-20 md:pb-0'
          : fullbleed
            ? // Mobile: white surface so the bottom-bar safe-area (pb-20) doesn't
              // reveal the grey shell bg; desktop stays transparent (Taxes split).
              'flex flex-col flex-1 min-h-0 overflow-y-auto md:overflow-hidden bg-card pb-20 md:bg-transparent md:pb-0'
            : flush
              ? // Same card, no inset — the page paints to its edges and owns
                // its own scrolling (see Flush).
                'm-2 flex flex-1 min-h-0 flex-col overflow-hidden rounded-xl border bg-background pb-20 md:ml-3 md:pb-0'
              : 'm-2 flex-1 min-h-0 overflow-y-auto rounded-xl border bg-background p-4 pb-20 md:ml-3 md:p-6 md:pb-6'
      }
    >
      {settingsWorkspace ? (
        <SettingsWorkspace>
          <Outlet />
        </SettingsWorkspace>
      ) : (
        <Outlet />
      )}
    </main>
  );
}
